/**
 * Шаг «Карточки» пряников: предпроверка «артикул × кабинет» (`POST /gingerbread/cards/plan`) с причинами по-русски
 * и создание черновиков (`POST /gingerbread/cards`). Публикации на площадки нет: черновики остаются в «Черновиках ПИМ».
 * Правило владельца: нет опубликованных визуалов кабинета — нет карточки.
 *
 * Профиль (`profile`): `gingerbread` по умолчанию (поведение пряников прежнее), `layout` — макеты из окна «Визуалы ИИ»
 * каталога товаров: те же маршруты с профилем в теле, другая подсказка над таблицей.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ExternalLink, FileText, Loader2, RefreshCw } from 'lucide-react'
import { Badge, Button } from '../../../components/ui'
import { SellerTargetTiles } from '../../../components/SellerTargetTiles'
import { useToastStore } from '../../../stores/toastStore'
import { gingerbreadVisualsFlowAPI } from './api'
import type { TargetSelection } from './hooks'
import {
  cardBlockerHint,
  chunk,
  DRAFTS_HREF,
  marketplaceShort,
  MAX_ARTICLES_PER_CALL,
  MAX_SELLERS_PER_CALL,
  mergePlans,
  OUTCOME_VIEW,
  planPairView,
} from './model'
import type { CardBlocker, CardsPlan, CardsResult, VisualsProfile } from './types'

export interface CardsStepProps {
  articles: string[]
  selection: TargetSelection
  /** Право `ContentFunnel:UPDATE`: без него создание закрыто, предпроверка остаётся. */
  canRun: boolean
  /** Профиль товаров; по умолчанию пряники. */
  profile?: VisualsProfile
}

const errorText = (error: unknown, fallback: string): string => {
  const value = error as { response?: { data?: { error?: { message?: string } } }; message?: string } | null
  return value?.response?.data?.error?.message || value?.message || fallback
}

const sectionTitle = 'mb-2 text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400'

/** Пачки вызовов: артикулы по 100 × кабинеты по 30. */
const callParts = (articles: string[], sellerIds: number[]): Array<{ articles: string[]; sellerIds: number[] }> =>
  chunk(articles, MAX_ARTICLES_PER_CALL).flatMap((part) =>
    chunk(sellerIds, MAX_SELLERS_PER_CALL).map((sellers) => ({ articles: part, sellerIds: sellers })),
  )

function Blockers({ blockers }: { blockers: CardBlocker[] }) {
  if (blockers.length === 0) return <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
  return (
    <ul className="space-y-1.5">
      {blockers.map((blocker, index) => {
        const hint = cardBlockerHint(blocker.code)
        return (
          <li key={`${blocker.code}-${index}`} className="text-xs text-gray-700 dark:text-zinc-300">
            <span className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
              <span>
                {blocker.message || blocker.code}
                {blocker.code && blocker.message && (
                  <> <span className="font-mono text-2xs text-gray-400 dark:text-zinc-500">({blocker.code})</span></>
                )}
              </span>
            </span>
            {hint && <span className="mt-0.5 block pl-5 text-gray-500 dark:text-zinc-400">Что сделать: {hint}</span>}
          </li>
        )
      })}
    </ul>
  )
}

export function CardsStep({ articles, selection, canRun, profile = 'gingerbread' }: CardsStepProps) {
  const isLayout = profile === 'layout'
  const showToast = useToastStore((state) => state.showToast)
  const { sellers, selected, selectedSellers, toggle } = selection

  const [plan, setPlan] = useState<CardsPlan | null>(null)
  const [planState, setPlanState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [planError, setPlanError] = useState('')
  const [creating, setCreating] = useState(false)
  const [result, setResult] = useState<CardsResult | null>(null)
  const sequence = useRef(0)

  const sellerIds = useMemo(() => selectedSellers.map((seller) => seller.seller_id), [selectedSellers])
  const sellerKey = sellerIds.join(',')
  const articlesKey = articles.join('|')

  const loadPlan = useCallback(async () => {
    const current = ++sequence.current
    if (sellerIds.length === 0 || articles.length === 0) {
      setPlan(null)
      setPlanState('idle')
      return
    }
    setPlanState('loading')
    setPlanError('')
    try {
      const parts: CardsPlan[] = []
      for (const part of callParts(articles, sellerIds)) {
        parts.push(await (isLayout
          ? gingerbreadVisualsFlowAPI.cardsPlan(part.articles, part.sellerIds, profile)
          : gingerbreadVisualsFlowAPI.cardsPlan(part.articles, part.sellerIds)))
      }
      if (current !== sequence.current) return
      setPlan(mergePlans(parts))
      setPlanState('ready')
    } catch (error) {
      if (current !== sequence.current) return
      setPlan(null)
      setPlanState('error')
      setPlanError(errorText(error, 'Не удалось проверить карточки'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articlesKey, sellerKey])

  useEffect(() => {
    if (sellers.loadState !== 'ready') return
    setResult(null)
    void loadPlan()
  }, [sellers.loadState, loadPlan])

  const pairs = useMemo(
    () => [...(plan?.pairs ?? [])].sort((a, b) => a.article.localeCompare(b.article, 'ru') || a.sellerId - b.sellerId),
    [plan],
  )
  const ready = pairs.filter((pair) => pair.ready).length
  const blocked = pairs.length - ready

  const createIssue: string | null = !canRun
    ? 'Нужно право «Воронки контента: изменение» (ContentFunnel:UPDATE)'
    : planState !== 'ready'
      ? 'Проверка ещё не завершена'
      : ready === 0
        ? 'Нет готовых пар: сначала опубликуйте визуалы'
        : null

  const create = async () => {
    if (createIssue || creating) return
    setCreating(true)
    try {
      const parts: CardsResult[] = []
      for (const part of callParts(articles, sellerIds)) {
        parts.push(await (isLayout
          ? gingerbreadVisualsFlowAPI.createCards(part.articles, part.sellerIds, profile)
          : gingerbreadVisualsFlowAPI.createCards(part.articles, part.sellerIds)))
      }
      const merged = mergePlans(parts)
      setResult(merged)
      const created = merged.pairs.filter((pair) => pair.status === 'created').length
      showToast(
        created > 0 ? `Черновиков создано: ${created.toLocaleString('ru-RU')}` : 'Ни один черновик не создан: смотрите причины',
        created > 0 ? 'success' : 'warning',
      )
      void loadPlan()
    } catch (error) {
      showToast(`Не удалось создать черновики: ${errorText(error, 'ошибка')}`, 'error')
    } finally {
      setCreating(false)
    }
  }

  const counts = result
    ? {
        created: result.pairs.filter((pair) => pair.status === 'created').length,
        skipped: result.pairs.filter((pair) => pair.status === 'skipped').length,
        blocked: result.pairs.filter((pair) => pair.status === 'blocked').length,
        failed: result.pairs.filter((pair) => pair.status === 'failed').length,
      }
    : null
  const resultMarketplaces = [...new Set((result?.pairs ?? []).filter((pair) => pair.status === 'created' || pair.cardId).map((pair) => pair.marketplace))]

  return (
    <div className="space-y-5" data-testid="gingerbread-cards-step" data-profile={profile}>
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">Карточки</h3>
        {isLayout ? (
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400" data-testid="cards-layout-hint">
            Черновики собираются из опубликованных визуалов кабинета — тех, что лежат в «Медиа» товара. Уже выгруженные
            на площадку карточки шаг не трогает: фото на площадке обновит действие «Переотправить фото». Публикации на
            площадки не будет: черновики появятся в «Черновиках ПИМ».
          </p>
        ) : (
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
            Черновики карточек создаются по опубликованным визуалам кабинета. Публикации на площадки не будет: черновики
            появятся в «Черновиках ПИМ».
          </p>
        )}
      </div>

      <div>
        <h4 className={sectionTitle}>Кабинеты</h4>
        <SellerTargetTiles
          targets={sellers.targets}
          loadState={sellers.loadState}
          loadError={sellers.loadError}
          unsupportedCount={sellers.unsupportedCount}
          selected={selected}
          onToggle={toggle}
          onRetry={() => void sellers.load()}
          emptySelectionHint="Выберите хотя бы один кабинет."
        />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h4 className={`${sectionTitle} mb-0`}>Предпроверка: артикул × кабинет</h4>
          <Button
            size="sm"
            variant="secondary"
            icon={<RefreshCw className="h-3.5 w-3.5" />}
            disabled={planState === 'loading' || sellerIds.length === 0}
            onClick={() => void loadPlan()}
          >
            Обновить проверку
          </Button>
        </div>
        {planState === 'loading' ? (
          <p role="status" className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Проверяем визуалы, цены и SEO-шаблоны…
          </p>
        ) : planState === 'error' ? (
          <p role="alert" className="text-xs text-red-600 dark:text-red-400">{planError}</p>
        ) : planState === 'idle' ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400">Выберите кабинеты, чтобы проверить карточки.</p>
        ) : (
          <>
            <p className="mb-2 text-xs text-gray-600 dark:text-zinc-300" data-testid="cards-plan-counts">
              Готово к созданию: {ready.toLocaleString('ru-RU')} из {pairs.length.toLocaleString('ru-RU')}
              {blocked > 0 ? `, заблокировано: ${blocked.toLocaleString('ru-RU')}` : ''}
            </p>
            {(plan?.rejectedArticles.length ?? 0) > 0 && (
              <ul className="mb-2 space-y-1 text-xs text-amber-700 dark:text-amber-300" aria-label="Отклонённые артикулы">
                {plan!.rejectedArticles.map((item) => (
                  <li key={item.subject}><span className="font-mono">{item.subject}</span>: {item.message}</li>
                ))}
              </ul>
            )}
            {(plan?.rejectedTargets.length ?? 0) > 0 && (
              <ul className="mb-2 space-y-1 text-xs text-amber-700 dark:text-amber-300" aria-label="Отклонённые кабинеты">
                {plan!.rejectedTargets.map((item) => (
                  <li key={item.subject}>Кабинет {item.subject}: {item.message}</li>
                ))}
              </ul>
            )}
            {pairs.length > 0 && (
              <div className="max-h-80 overflow-auto rounded-lg border border-gray-200 dark:border-zinc-800">
                <table className="w-full text-left text-sm" aria-label="Предпроверка карточек по кабинетам">
                  <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500 dark:bg-zinc-900 dark:text-zinc-400">
                    <tr>
                      <th scope="col" className="px-3 py-2 font-medium">Артикул</th>
                      <th scope="col" className="px-3 py-2 font-medium">Кабинет</th>
                      <th scope="col" className="px-3 py-2 font-medium">Состояние</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">Фото</th>
                      <th scope="col" className="px-3 py-2 font-medium">Причины</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                    {pairs.map((pair) => {
                      const view = planPairView(pair)
                      return (
                        <tr key={`${pair.article}:${pair.sellerId}`} className="align-top">
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-sm text-gray-900 dark:text-zinc-100">{pair.article}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-zinc-300">
                            {marketplaceShort(pair.marketplace)} · {pair.sellerName || pair.cabinet}
                          </td>
                          <td className="px-3 py-2"><Badge variant={view.variant}>{view.label}</Badge></td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-zinc-300">{pair.photos > 0 ? pair.photos : '—'}</td>
                          <td className="px-3 py-2"><Blockers blockers={pair.blockers} /></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          icon={<FileText className="h-4 w-4" />}
          loading={creating}
          disabled={Boolean(createIssue)}
          title={createIssue ?? undefined}
          onClick={() => void create()}
        >
          Создать черновики
        </Button>
        {createIssue && !creating && <p role="note" className="text-xs text-amber-700 dark:text-amber-300">{createIssue}</p>}
      </div>

      {result && counts && (
        <div role="status" className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300" data-testid="cards-result">
          <p className="font-medium">
            Итог: создано {counts.created.toLocaleString('ru-RU')}
            {counts.skipped > 0 ? `, пропущено ${counts.skipped.toLocaleString('ru-RU')}` : ''}
            {counts.blocked > 0 ? `, заблокировано ${counts.blocked.toLocaleString('ru-RU')}` : ''}
            {counts.failed > 0 ? `, ошибок ${counts.failed.toLocaleString('ru-RU')}` : ''}
          </p>
          <ul className="space-y-1" aria-label="Итог по парам">
            {result.pairs.filter((pair) => pair.status !== 'created').map((pair) => (
              <li key={`${pair.article}:${pair.sellerId}`} className="flex flex-wrap items-start gap-2">
                <span className="font-mono">{pair.article}</span>
                <span>{marketplaceShort(pair.marketplace)} · {pair.sellerName || pair.cabinet}</span>
                <Badge variant={OUTCOME_VIEW[pair.status].variant}>{OUTCOME_VIEW[pair.status].label}</Badge>
                {pair.blockers.length > 0 && <span className="basis-full pl-1"><Blockers blockers={pair.blockers} /></span>}
              </li>
            ))}
          </ul>
          {resultMarketplaces.length > 0 && (
            <p className="flex flex-wrap items-center gap-3">
              {resultMarketplaces.map((marketplace) => (
                <Link
                  key={marketplace}
                  to={DRAFTS_HREF[marketplace] ?? '/wildberries'}
                  title="Откроется каталог площадки: вкладка «Черновики PIM»"
                  className="inline-flex items-center gap-1 text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  Открыть в «Черновиках ПИМ»: {marketplaceShort(marketplace)}
                </Link>
              ))}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
